/**
 * Focus « En direct » (specs/01 §3). Mise en page en DEUX cartes côte à côte :
 *  - GRANDE carte (gauche) : match EN DIRECT, sinon le DERNIER match terminé
 *    (score + stats finales conservées) tant qu'aucun nouveau match n'a démarré.
 *  - PETITE carte « prochain match » (droite) : toujours visible, qui BAT comme
 *    un cœur pour inviter au survol ; au survol elle s'ÉTEND à ~la moitié pour
 *    dévoiler les stats AVANT-MATCH (prédiction du modèle : 1N2, Elo, xG), puis
 *    se rétracte. Avant tout match (pas de direct ni de terminé) : la grande
 *    carte « prochain » seule. Plus rien à venir : la zone se replie.
 */
import type { MatchView, OfficialState } from "../../app/officialStore.ts";
import { esc, flagSpan, flagCode, teamName, roundName, playerLink } from "../html.ts";
import { t } from "../../app/i18n.ts";
import { teamRatingTag, teamTrendTag } from "../teamRatingTag.ts";
import { fmtKick, fmtCountdown, COUNTDOWN_STALE_MS, fmtPct } from "../../app/format.ts";
import { duoStat } from "./statBars.ts";
import { statMeta, STAT_ORDER } from "./statGraph.ts";
import { lineupPitch } from "./lineupPitch.ts";
import { lambdas, outcomeProbabilities } from "../../engine/poisson.ts";
import { initialElos, hosts, poissonParams, modelHomeAdvantage } from "../../app/data.ts";

export interface LiveFocusState {
  pinned: number | null;
  rotationIndex: number;
}

export function pickFocused(live: readonly MatchView[], focus: LiveFocusState): MatchView | null {
  if (live.length === 0) return null;
  if (focus.pinned !== null) {
    const pinned = live.find((v) => v.match.id === focus.pinned);
    if (pinned) return pinned;
  }
  return live[focus.rotationIndex % live.length] ?? live[0] ?? null;
}

/** Prochain match programmé (coup d'envoi le plus tôt), live exclu. */
export function pickNextMatch(state: OfficialState): MatchView | null {
  let best: MatchView | null = null;
  let bestT = Infinity;
  for (const v of state.matches) {
    if (v.status !== "scheduled") continue;
    const t = Date.parse(v.match.utcDateTime);
    if (Number.isFinite(t) && t < bestT) {
      bestT = t;
      best = v;
    }
  }
  return best;
}

/** TOUS les prochains matchs au coup d'envoi le plus tôt (≥ 2 si plusieurs démarrent
 *  à la même heure). Triés par id pour un ordre stable. */
export function pickNextMatches(state: OfficialState): MatchView[] {
  let bestT = Infinity;
  for (const v of state.matches) {
    if (v.status !== "scheduled") continue;
    const t = Date.parse(v.match.utcDateTime);
    if (Number.isFinite(t) && t < bestT) bestT = t;
  }
  if (!Number.isFinite(bestT)) return [];
  return state.matches
    .filter((v) => v.status === "scheduled" && Date.parse(v.match.utcDateTime) === bestT)
    .sort((a, b) => a.match.id - b.match.id);
}

/** Dernier match TERMINÉ (coup d'envoi le plus tardif). */
export function pickLastFinished(state: OfficialState): MatchView | null {
  let best: MatchView | null = null;
  let bestT = -Infinity;
  for (const v of state.matches) {
    if (v.status !== "finished") continue;
    const t = Date.parse(v.match.utcDateTime);
    if (Number.isFinite(t) && t > bestT) {
      bestT = t;
      best = v;
    }
  }
  return best;
}

/**
 * Match IMMINENT dont la compo est déjà publiée (coup d'envoi le plus tôt). Le
 * serveur ne récupère les compos que pour les matchs proches du coup d'envoi,
 * donc « programmé + compo présente » ⟺ imminent : pas besoin de re-calculer le
 * délai ici. C'est lui qui prend la grande carte dès que les compos tombent.
 */
export function pickPreview(state: OfficialState): MatchView | null {
  let best: MatchView | null = null;
  let bestT = Infinity;
  for (const v of state.matches) {
    if (v.status !== "scheduled" || !v.live?.lineups) continue;
    const t = Date.parse(v.match.utcDateTime);
    if (Number.isFinite(t) && t < bestT) {
      bestT = t;
      best = v;
    }
  }
  return best;
}

export type FocusMode = "live" | "preview" | "finished" | "next" | "none";
export interface FocusDecision {
  readonly mode: FocusMode;
  readonly match: MatchView | null;
}

/**
 * Quelle GRANDE carte ? Direct > match imminent dont la COMPO est tombée (la
 * carte passe alors en grand et remplace le dernier terminé) > dernier match
 * terminé > prochain match (rien joué encore) > rien. Pur → testable hors DOM.
 */
export function focusMain(state: OfficialState, focus: LiveFocusState): FocusDecision {
  const focused = pickFocused(state.liveNow, focus);
  if (focused) return { mode: "live", match: focused };
  const preview = pickPreview(state);
  if (preview) return { mode: "preview", match: preview };
  const last = pickLastFinished(state);
  if (last) return { mode: "finished", match: last };
  const next = pickNextMatch(state);
  if (next) return { mode: "next", match: next };
  return { mode: "none", match: null };
}

/** Codes domicile/extérieur résolus (groupe direct, KO via le tableau) ou null. */
function resolvedCodes(state: OfficialState, v: MatchView): [string | null, string | null] {
  let home: string | null = v.match.home;
  let away: string | null = v.match.away;
  if (v.match.stage !== "group") {
    const node = state.bracket["M" + String(v.match.id)];
    home = node?.a ?? null;
    away = node?.b ?? null;
  }
  const ok = (c: string | null): string | null => (c && /^[A-Z]{3}$/.test(c) ? c : null);
  return [ok(home), ok(away)];
}

/** Code d'équipe résolu d'un côté : le slot du bracket pour un match KO déjà tranché,
 *  sinon le code du calendrier (placeholder « 2A » tant que le KO n'est pas résolu).
 *  Sans ça, un match KO live affiche des drapeaux/équipes vides (jeton de slot). */
function sideCode(state: OfficialState, v: MatchView, side: "home" | "away"): string {
  const schedule = side === "home" ? v.match.home : v.match.away;
  if (v.match.stage === "group") return schedule;
  const node = state.bracket["M" + String(v.match.id)];
  const resolved = node ? (side === "home" ? node.a : node.b) : null;
  return resolved ?? schedule;
}

/** Drapeau + nom si l'équipe est connue, sinon le jeton de slot KO (grisé). */
function teamCell(state: OfficialState, v: MatchView, side: "home" | "away"): string {
  let code = side === "home" ? v.match.home : v.match.away;
  if (v.match.stage !== "group") {
    const node = state.bracket["M" + String(v.match.id)];
    const resolved = node ? (side === "home" ? node.a : node.b) : null;
    if (resolved) code = resolved;
  }
  return /^[A-Z]{3}$/.test(code)
    ? flagSpan(code, { name: true })
    : `<span class="cd dim">${esc(code)}</span>`;
}

interface PreModel {
  readonly homeWin: number;
  readonly draw: number;
  readonly awayWin: number;
  readonly eloH: number;
  readonly eloA: number;
  readonly xgH: number;
  readonly xgA: number;
}

/** Prédiction AVANT-MATCH du modèle (1N2, Elo, xG). null si adversaires inconnus. */
function preMatchModel(state: OfficialState, v: MatchView): PreModel | null {
  const [home, away] = resolvedCodes(state, v);
  if (!home || !away) return null;
  const eloH = state.currentElos[home] ?? initialElos[home];
  const eloA = state.currentElos[away] ?? initialElos[away];
  if (eloH === undefined || eloA === undefined) return null;
  const m = v.match;
  const effH =
    eloH + (m.hostCountry != null && hosts[home] === m.hostCountry ? modelHomeAdvantage : 0);
  const effA =
    eloA + (m.hostCountry != null && hosts[away] === m.hostCountry ? modelHomeAdvantage : 0);
  const p = outcomeProbabilities(effH, effA, poissonParams);
  const [xgH, xgA] = lambdas(effH, effA, poissonParams);
  return { homeWin: p.homeWin, draw: p.draw, awayWin: p.awayWin, eloH, eloA, xgH, xgA };
}

/** PETITE carte « prochain match » (droite) : résumé compact toujours visible +
 *  prédiction avant-match révélée au survol (l'extension est gérée en CSS). */
function miniNextHtml(state: OfficialState, next: MatchView, nowMs: number): string {
  const iso = next.match.utcDateTime;
  const [hc, ac] = resolvedCodes(state, next);
  const model = preMatchModel(state, next);
  const cd = fmtCountdown(Date.parse(iso) - nowMs).replace(/^dans\s+/, ""); // compact, sans « dans »
  const flag = (c: string | null): string =>
    c ? flagSpan(c, {}) : `<span class="cd dim">?</span>`;
  // En-tête (drapeaux) montré UNE seule fois ; le survol ne fait qu'ajouter la
  // prédiction dessous (plus de drapeaux dupliqués).
  const full = model
    ? `<div class="lnm-sub dim">${esc(hc ?? "?")} – ${esc(ac ?? "?")} · ${t("lf.modelPrediction")}</div>
       <div class="lnm-prow"><span>${fmtPct(model.homeWin, 0)}</span><span class="mid">${t("common.draw")} ${fmtPct(model.draw, 0)}</span><span>${fmtPct(model.awayWin, 0)}</span></div>
       <div class="lnm-pbar"><span class="h" style="width:${(model.homeWin * 100).toFixed(1)}%"></span><span class="d" style="width:${(model.draw * 100).toFixed(1)}%"></span><span class="a" style="width:${(model.awayWin * 100).toFixed(1)}%"></span></div>
       ${duoStat(t("lf.strengthElo"), [Math.round(model.eloH), Math.round(model.eloA)], false, "lf-st")}
       ${duoStat(t("lf.expectedGoals"), [model.xgH, model.xgA], false, "lf-st")}
       <div class="lnm-kick dim">${esc(fmtKick(iso))}</div>`
    : `<div class="lnm-note dim">${t("lf.opponentsUnknown")}</div>`;
  return `
    <aside class="lf-next-mini" data-match="${next.match.id}" tabindex="0" aria-label="${t("lf.nextMiniAria")}">
      <div class="lnm-label"><i class="ti ti-clock" aria-hidden="true"></i> ${t("lf.nextShort")}</div>
      <div class="lnm-flags">${flag(hc)}<span class="lnm-x">·</span>${flag(ac)}</div>
      <div class="lnm-cd">${esc(cd)}</div>
      <i class="ti ti-chevron-down lnm-chev" aria-hidden="true"></i>
      <div class="lnm-full">${full}</div>
    </aside>`;
}

/** GRANDE carte « prochain match » SEULE (avant tout match joué). */
function nextBigHtml(state: OfficialState, next: MatchView, nowMs: number): string {
  const iso = next.match.utcDateTime;
  const stale = Date.parse(iso) - nowMs < -COUNTDOWN_STALE_MS;
  const phase =
    next.match.stage === "group"
      ? t("lf.groupMatchday", { group: next.match.group ?? "", matchday: next.match.matchday ?? "" })
      : roundName(next.match.stage);
  const venue = [next.match.city, next.match.stadium].filter(Boolean).join(" · ");
  return `
    <article class="live-focus next${stale ? " stale" : ""}" data-match="${next.match.id}">
      <div class="lf-top"><span class="lf-soon"><i class="ti ti-clock" aria-hidden="true"></i> ${stale ? t("lf.kickoffPassed") : t("lf.nextMatch")}</span> · ${esc(phase)}</div>
      <div class="lf-main">
        <div class="lf-team">${teamCell(state, next, "home")}</div>
        <div class="lf-vs">${t("common.vs")}</div>
        <div class="lf-team away">${teamCell(state, next, "away")}</div>
      </div>
      <div class="lf-cd-row"><span class="lf-cd" data-kick="${esc(iso)}">${esc(fmtCountdown(Date.parse(iso) - nowMs))}</span></div>
      <div class="lf-kick dim">${stale ? t("lf.kickoffWaitingLive", { kick: esc(fmtKick(iso)) }) : t("lf.kickoffYourLocalTime", { kick: esc(fmtKick(iso)) })}</div>
      ${venue ? `<div class="lf-venue dim">${esc(venue)}</div>` : ""}
    </article>`;
}

const EV_ICON: Record<string, string> = {
  goal: `<i class="ti ti-ball-football" aria-hidden="true"></i>`,
  yellow: `<span class="ev-card yellow" aria-hidden="true"></span>`,
  red: `<span class="ev-card red" aria-hidden="true"></span>`,
  subst: `<i class="ti ti-arrows-exchange-2" aria-hidden="true"></i>`,
};
const evLabel = (): Record<string, string> => ({
  goal: t("lf.event.goal"),
  yellow: t("lf.event.yellow"),
  red: t("lf.event.red"),
  subst: t("lf.event.subst"),
});

/** Timeline des évènements (buts, cartons, remplacements) triés par minute. Les
 *  buts montrent le passeur ; pour un remplacement, `player` est le SORTANT (↓,
 *  titulaire remplacé) et `assist` l'ENTRANT (↑, le remplaçant). */
export function eventsHtml(state: OfficialState, v: MatchView): string {
  const evs = (v.live?.events ?? [])
    .filter((e) => e.type in EV_ICON)
    .slice()
    .sort((a, b) => (a.minute ?? 0) - (b.minute ?? 0));
  if (!evs.length) return "";
  const rows = evs
    .map((e) => {
      const code = sideCode(state, v, e.team);
      const min = e.minute != null ? `${e.minute}${e.extra ? `+${e.extra}` : ""}′` : "";
      const second =
        e.type === "goal" && e.assist
          ? `<span class="ev-sub">${t("lf.assistBy", { player: playerLink(e.assist, e.assistId) })}</span>`
          : e.type === "subst" && e.assist
            ? `<span class="ev-sub"><span class="ev-in">↑</span> ${playerLink(e.assist, e.assistId)}</span>`
            : "";
      const lead = e.type === "subst" ? `<span class="ev-out">↓</span> ` : "";
      return `<div class="ev-row" title="${esc(evLabel()[e.type] ?? "")} ${esc(min)}">
        <span class="ev-min">${esc(min)}</span>
        <span class="f fi fi-${esc(flagCode(code))} ev-flag"></span>
        <span class="ev-ico ev-${esc(e.type)}">${EV_ICON[e.type] ?? ""}</span>
        <span class="ev-name">${lead}${playerLink(e.player, e.playerId)}${second ? ` ${second}` : ""}</span>
      </div>`;
    })
    .join("");
  return `<div class="lf-events tl">${rows}</div>`;
}

/** Stats RÉELLES (API-Football) sous le score, si le flux les fournit. */
function liveStatsHtml(v: MatchView): string {
  const s = v.live?.stats;
  if (!s) return "";
  const meta = statMeta();
  const rows = STAT_ORDER.map((k) =>
    duoStat(meta[k]?.label ?? k, s[k], meta[k]?.asPct ?? false, "lf-st", k),
  ).join("");
  return rows ? `<div class="lf-stats">${rows}</div>` : "";
}

/**
 * Anime les barres de stats vers leur nouvelle valeur (glissement en cascade,
 * 70 ms/ligne). Une barre inchangée ne bouge pas. Respecte prefers-reduced-motion.
 */
const prevShare = new Map<string, number>();
export function animateStatBars(host: HTMLElement, matchId: number): void {
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  const rows = host.querySelectorAll<HTMLElement>(".lf-stats .lf-st-sec[data-stat]");
  rows.forEach((row, i) => {
    const h = row.querySelector<HTMLElement>(".lf-st-bar .h");
    const a = row.querySelector<HTMLElement>(".lf-st-bar .a");
    if (!h || !a) return;
    const target = Number.parseFloat(h.style.width);
    if (!Number.isFinite(target)) return;
    const key = `${matchId}:${row.dataset.stat ?? String(i)}`;
    const start = reduce ? target : (prevShare.get(key) ?? 50);
    prevShare.set(key, target);
    if (start === target) return;
    h.style.transition = a.style.transition = "none";
    h.style.width = `${start}%`;
    a.style.width = `${100 - start}%`;
    void host.offsetWidth;
    h.style.transition = a.style.transition = "";
    h.style.transitionDelay = a.style.transitionDelay = `${i * 70}ms`;
    requestAnimationFrame(() => {
      h.style.width = `${target}%`;
      a.style.width = `${100 - target}%`;
    });
  });
}

// --- Blocs PLIABLES de la grande carte (demande utilisateur) ---------------
// La carte live se reconstruit à chaque tick → l'état plié/déplié est une
// PRÉFÉRENCE persistée (localStorage) qui PILOTE le rendu (sinon il se rouvrirait
// tout seul). Défauts : Compositions repliée (énorme), Stats repliées sur mobile.
const COLLAPSE_KEY = "wc26.lf.collapsed";
let collapsePref: Record<string, boolean> | null = null;
function loadCollapse(): Record<string, boolean> {
  if (collapsePref) return collapsePref;
  try {
    collapsePref = JSON.parse(localStorage.getItem(COLLAPSE_KEY) ?? "{}") as Record<
      string,
      boolean
    >;
  } catch {
    collapsePref = {};
  }
  return collapsePref ?? (collapsePref = {});
}
function isCollapsed(block: string): boolean {
  const pref = loadCollapse();
  if (block in pref) return Boolean(pref[block]);
  if (block === "lineups") return true; // compositions repliées par défaut
  if (block === "stats") return true; // statistiques repliées par défaut (seuls les évènements restent ouverts)
  return false;
}
function setCollapsed(block: string, v: boolean): void {
  const pref = loadCollapse();
  pref[block] = v;
  try {
    localStorage.setItem(COLLAPSE_KEY, JSON.stringify(pref));
  } catch {
    /* stockage indispo → état de session uniquement */
  }
}

// Par DÉFAUT, tous les matchs en direct partagent l'écran (grandes cartes côte à
// côte). Ce Set contient ceux que l'utilisateur a explicitement RÉDUITS en mini-carte
// (pour se concentrer sur les autres). Vide = tout en grand. Éphémère (réinit. au
// rechargement) : choix d'affichage transitoire.
const minimizedLives = new Set<number>();

/** Enveloppe un bloc dans un conteneur pliable : en-tête-bouton (titre + chevron)
 *  + corps animé (grid-rows 1fr↔0fr, robuste même pour le terrain). */
function collapsibleBlock(block: string, label: string, icon: string, body: string): string {
  const collapsed = isCollapsed(block);
  return `<div class="lf-block lf-block-${block}${collapsed ? " collapsed" : ""}">
    <button type="button" class="lf-sec-h lf-toggle" data-collapse="${block}" aria-expanded="${!collapsed}">
      <i class="ti ${icon}" aria-hidden="true"></i><span class="lf-toggle-l">${esc(label)}</span><i class="ti ti-chevron-down lf-chev" aria-hidden="true"></i>
    </button>
    <div class="lf-block-body"><div class="lf-block-inner">${body}</div></div>
  </div>`;
}

/** Bloc STATISTIQUES de la carte live, PLIABLE (réutilise liveStatsHtml inchangé). */
function statsBlockLF(v: MatchView): string {
  const inner = liveStatsHtml(v);
  return inner ? collapsibleBlock("stats", t("lf.stats"), "ti-chart-bar", inner) : "";
}

/** Bloc COMPOSITIONS de la carte live, PLIABLE (mini-terrain) ; message discret
 *  non pliable si la compo n'est pas communiquée. (lineupsSection reste à part
 *  pour le panneau de match, non pliable.) */
function lineupsBlockLF(v: MatchView): string {
  const lu = v.live?.lineups;
  if (!lu || (!lu.home && !lu.away)) {
    return `<div class="lp-none"><i class="ti ti-clipboard-off" aria-hidden="true"></i> ${esc(t("lf.lineupsNone"))}</div>`;
  }
  return collapsibleBlock(
    "lineups",
    t("lf.lineups"),
    "ti-clipboard-list",
    lineupPitch(v.match.home, v.match.away, lu.home, lu.away),
  );
}

/** GRANDE carte d'un match : EN DIRECT (point clignotant + minute + rotation) ou
 *  TERMINÉ (✓, score + stats finales conservées). */
function bigCardHtml(state: OfficialState, v: MatchView, isLive: boolean, canMinimize = false): string {
  const [a, b] = (isLive ? v.live?.score?.ft : v.displayScore) ?? v.live?.score?.ft ?? [0, 0];
  const homeCode = sideCode(state, v, "home");
  const awayCode = sideCode(state, v, "away");
  const phase =
    v.match.stage === "group"
      ? t("lf.groupMatchday", { group: v.match.group ?? "", matchday: v.match.matchday ?? "" })
      : roundName(v.match.stage);
  const minute =
    v.status === "paused" ? t("common.halfTime") : v.minute != null ? `${v.minute}${v.addedTime ? `+${v.addedTime}` : ""}′` : t("lf.inProgress");
  const liveInner = `<span class="dot"></span> ${t("common.live")} · ${esc(phase)} <span class="lf-minute">${esc(minute)}</span>`;
  // Bouton « réduire » : en mode partagé (≥ 2 grandes cartes), chaque match peut
  // repasser en mini-carte (l'utilisateur se concentre sur les autres).
  const minBtn = `<button type="button" class="lf-min" data-minimize="${v.match.id}" title="${t("lf.minimizeMatch")}" aria-label="${t("lf.minimizeMatch")}"><i class="ti ti-arrows-minimize" aria-hidden="true"></i></button>`;
  const top = !isLive
    ? `<div class="lf-top done"><i class="ti ti-circle-check" aria-hidden="true"></i> ${t("common.finished")} · ${esc(phase)}</div>`
    : canMinimize
      ? `<div class="lf-top split">${liveInner}${minBtn}</div>`
      : `<div class="lf-top">${liveInner}</div>`;
  const pens =
    !isLive && v.penalties
      ? `<div class="lf-ht dim">${t("lf.penaltiesScore", { h: v.penalties.score[0], a: v.penalties.score[1] })}</div>`
      : "";
  return `
    <article class="live-focus${isLive ? "" : " ended"}" data-match="${v.match.id}">
      ${top}
      <div class="lf-main">
        <div class="lf-team">${flagSpan(homeCode, { name: true })}${teamRatingTag(v.match.id, homeCode) || teamTrendTag(homeCode)}</div>
        <div class="lf-score">${a} – ${b}</div>
        <div class="lf-team away">${flagSpan(awayCode, { name: true })}${teamRatingTag(v.match.id, awayCode) || teamTrendTag(awayCode)}</div>
      </div>
      ${v.live?.score?.ht ? `<div class="lf-ht dim">${t("lf.halfTimeScore", { h: v.live.score.ht[0], a: v.live.score.ht[1] })}</div>` : ""}
      ${pens}
      ${eventsHtml(state, v)}
      ${statsBlockLF(v)}
      ${lineupsBlockLF(v)}
      <div class="lf-venue dim">${esc(v.match.stadium ?? "")}${v.match.city ? ` · ${esc(v.match.city)}` : ""}</div>
    </article>`;
}

/** Mini-carte d'un AUTRE match en direct (plusieurs simultanés) : minute + score
 *  + drapeaux, cliquable pour le passer en grande carte (focus). */
function liveMiniHtml(state: OfficialState, v: MatchView): string {
  const [a, b] = v.live?.score?.ft ?? [0, 0];
  const home = sideCode(state, v, "home");
  const away = sideCode(state, v, "away");
  const minute = v.status === "paused" ? t("common.halfTimeAbbr") : v.minute != null ? `${v.minute}${v.addedTime ? `+${v.addedTime}` : ""}′` : "live";
  return `<button type="button" class="lf-live-mini" data-expand="${v.match.id}" title="${t("lf.expandMiniTitle", { home: esc(teamName(home)), away: esc(teamName(away)) })}">
      <span class="llm-min"><span class="dot"></span>${esc(minute)}</span>
      <span class="llm-team">${flagSpan(home, {})}<b>${a}</b></span>
      <span class="llm-team">${flagSpan(away, {})}<b>${b}</b></span>
    </button>`;
}

/** Section « Compositions » (mini-terrain) d'une grande carte. Si la source n'a
 *  pas la compo (fréquent sur des fixtures WC encore incomplètes), un petit
 *  message discret le signale plutôt que de ne rien montrer. */
export function lineupsSection(v: MatchView): string {
  const lu = v.live?.lineups;
  if (!lu || (!lu.home && !lu.away)) {
    return `<div class="lp-none"><i class="ti ti-clipboard-off" aria-hidden="true"></i> ${t("lf.lineupsNotProvided")}</div>`;
  }
  return `<div class="lf-lineups">
    <div class="lf-sec-h"><i class="ti ti-clipboard-list" aria-hidden="true"></i> ${t("lf.lineupsTitle")}</div>
    ${lineupPitch(v.match.home, v.match.away, lu.home, lu.away)}
  </div>`;
}

/** GRANDE carte AVANT-MATCH : compos tombées → le terrain tactique prend la
 *  vedette (remplace le dernier terminé), + prédiction du modèle + coup d'envoi. */
function previewBigHtml(state: OfficialState, v: MatchView, nowMs: number): string {
  const lu = v.live?.lineups;
  const [hc, ac] = resolvedCodes(state, v);
  const model = preMatchModel(state, v);
  const cd = fmtCountdown(Date.parse(v.match.utcDateTime) - nowMs);
  const phase =
    v.match.stage === "group"
      ? t("lf.groupMatchdayAbbr", { group: v.match.group ?? "", matchday: v.match.matchday ?? "" })
      : roundName(v.match.stage);
  const pred = model
    ? `<div class="lf-pred">
        <div class="lf-prow"><span>${fmtPct(model.homeWin, 0)}</span><span class="mid">${t("lf.predictionDraw", { draw: fmtPct(model.draw, 0) })}</span><span>${fmtPct(model.awayWin, 0)}</span></div>
        <div class="lf-pbar"><span class="h" style="width:${(model.homeWin * 100).toFixed(0)}%"></span><span class="d" style="width:${(model.draw * 100).toFixed(0)}%"></span><span class="a" style="width:${(model.awayWin * 100).toFixed(0)}%"></span></div>
      </div>`
    : "";
  return `<article class="live-focus preview" data-match="${v.match.id}">
    <div class="lf-top soon"><i class="ti ti-clipboard-list" aria-hidden="true"></i> ${t("lf.lineupsUpper")} · ${esc(phase)} <span class="lf-minute">${esc(fmtKick(v.match.utcDateTime))} · ${esc(cd)}</span></div>
    <div class="lf-main">
      <div class="lf-team">${teamCell(state, v, "home")}</div>
      <div class="lf-score time">${t("lf.kickoffShort")}</div>
      <div class="lf-team away">${teamCell(state, v, "away")}</div>
    </div>
    ${lu ? lineupPitch(hc ?? v.match.home, ac ?? v.match.away, lu.home, lu.away) : ""}
    ${pred}
    <div class="lf-venue dim">${esc(v.match.stadium ?? "")}${v.match.city ? ` · ${esc(v.match.city)}` : ""}</div>
  </article>`;
}

export function renderLiveFocus(
  el: HTMLElement,
  state: OfficialState,
  focus: LiveFocusState,
  nowMs: number = Date.now(),
): void {
  const main = focusMain(state, focus);
  if (main.mode === "none" || !main.match) {
    el.innerHTML = "";
    el.classList.add("hide");
    return;
  }
  el.classList.remove("hide");

  // Avant tout match joué : grande(s) carte(s) « prochain ». Si plusieurs matchs
  // démarrent à la même heure, ils PARTAGENT l'écran (comme les directs simultanés).
  if (main.mode === "next") {
    const nexts = pickNextMatches(state);
    const list = nexts.length ? nexts : [main.match];
    const cards = list.map((m) => nextBigHtml(state, m, nowMs)).join("");
    el.innerHTML = `<div class="lf-zone${list.length > 1 ? " split" : " solo"}">${cards}</div>`;
    return;
  }

  // Compos tombées (match imminent) : le terrain tactique prend toute la carte.
  if (main.mode === "preview") {
    el.innerHTML = `<div class="lf-zone solo">${previewBigHtml(state, main.match, nowMs)}</div>`;
    return;
  }

  // Direct / terminé → une OU plusieurs grandes cartes. En direct simultané,
  // l'utilisateur PARTAGE l'espace : clic sur une mini-carte → ce match passe aussi
  // en grand ; bouton « réduire » → il revient en mini-carte (choix : un ou plusieurs).
  const v = main.match;
  const nexts = pickNextMatches(state); // tous les prochains à la même heure (mini-cartes)
  const isLive = main.mode === "live";

  // Par DÉFAUT, TOUS les lives partagent l'écran (grandes cartes côte à côte) ;
  // minimizedLives = ceux réduits par l'utilisateur. Hors live : une seule carte.
  const liveIds = new Set(state.liveNow.map((m) => m.match.id));
  for (const id of [...minimizedLives]) if (!liveIds.has(id)) minimizedLives.delete(id); // purge des finis
  if (state.liveNow.length <= 1) minimizedLives.clear(); // un seul direct → toujours en grand
  let bigViews: MatchView[] = [v];
  if (isLive) {
    // Plus de repli forcé : avec ≥ 2 directs, l'utilisateur peut TOUS les réduire
    // (0 grande carte → tout en mini-cartes), pour ne garder que des résumés compacts.
    bigViews = state.liveNow.filter((m) => !minimizedLives.has(m.match.id));
  }
  const split = bigViews.length > 1;
  const allMini = isLive && bigViews.length === 0; // tous les directs réduits → uniquement des minis
  const canMinimize = isLive && state.liveNow.length > 1; // « réduire » dès qu'il y a ≥ 2 directs (même le dernier en grand)
  const bigIds = new Set(bigViews.map((m) => m.match.id));
  const otherLives = isLive ? state.liveNow.filter((m) => !bigIds.has(m.match.id)) : [];
  const minis =
    otherLives.map((m) => liveMiniHtml(state, m)).join("") +
    nexts.map((m) => miniNextHtml(state, m, nowMs)).join("");

  // Recalage du flux d'évènements en bas (ticker). Une seule carte : on respecte le
  // défilement manuel. Partagé : on cale chaque flux en bas (les cartes sont neuves).
  const prevEv = el.querySelector<HTMLElement>(".lf-events");
  const prevMatch = el.querySelector<HTMLElement>(".live-focus")?.dataset.match;
  const keepBottom =
    !split &&
    (!prevEv ||
      prevMatch !== String(v.match.id) ||
      prevEv.scrollHeight - prevEv.scrollTop - prevEv.clientHeight < 24);

  const bigs = bigViews.map((bv) => bigCardHtml(state, bv, isLive, canMinimize)).join("");
  el.innerHTML = `<div class="lf-zone${split ? " split" : ""}${allMini ? " allmini" : ""}${minis || split || allMini ? "" : " solo"}">${bigs}${minis ? `<div class="lf-minis">${minis}</div>` : ""}</div>`;
  for (const bv of bigViews) animateStatBars(el, bv.match.id);
  if (split) {
    el.querySelectorAll<HTMLElement>(".lf-events").forEach((box) => (box.scrollTop = box.scrollHeight));
  } else {
    const evBox = el.querySelector<HTMLElement>(".lf-events");
    if (evBox && keepBottom) evBox.scrollTop = evBox.scrollHeight;
  }

  // Blocs pliables (Compositions / Statistiques) : bascule + mémorise.
  el.querySelectorAll<HTMLElement>("[data-collapse]").forEach((btn) => {
    btn.onclick = () => {
      const block = btn.dataset.collapse;
      if (!block) return;
      const nextC = !isCollapsed(block);
      setCollapsed(block, nextC);
      btn.closest(".lf-block")?.classList.toggle("collapsed", nextC);
      btn.setAttribute("aria-expanded", String(!nextC));
    };
  });

  if (isLive) {
    // Clic sur une mini-carte → ce match REVIENT en grand (re-partage l'écran).
    el.querySelectorAll<HTMLElement>("[data-expand]").forEach((btn) => {
      btn.onclick = () => {
        minimizedLives.delete(Number(btn.dataset.expand));
        renderLiveFocus(el, state, focus);
      };
    });
    // « Réduire » → ce match passe en mini-carte. Avec ≥ 2 directs, on peut les
    // réduire TOUS (0 grande carte) ; clic sur une mini en ré-affiche un en grand.
    el.querySelectorAll<HTMLElement>("[data-minimize]").forEach((btn) => {
      btn.onclick = () => {
        if (state.liveNow.length > 1) minimizedLives.add(Number(btn.dataset.minimize));
        renderLiveFocus(el, state, focus);
      };
    });
  }
}
